import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowRight, Users, Calendar, Wallet, Building, Clock, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_INFO = {
  familia: {
    title: "Viajes de Familia",
    subtitle: "Crea recuerdos inolvidables con los tuyos",
    description: "Los viajes en familia son los más importantes porque crean recuerdos que perduran para siempre. Somos expertos en diseñar experiencias que todos disfrutaréis, desde los más pequeños hasta los abuelos. Cada detalle está pensado para que vuestra escapada sea perfecta.",
    image: "https://images.unsplash.com/photo-1602002418816-5c0aeef426aa?auto=format&fit=crop&q=80&w=2000"
  },
  novios: {
    title: "Viajes de Novios",
    subtitle: "El viaje más especial de vuestra vida",
    description: "Vuestra luna de miel merece ser perfecta. Diseñamos experiencias románticas únicas que se adaptan a vuestros sueños. Desde playas paradisíacas hasta ciudades con encanto, cada momento será especial.",
    image: "https://images.unsplash.com/photo-1544078751-58fee2d8a03b?auto=format&fit=crop&q=80&w=2000"
  },
  grupos: {
    title: "Viajes de Grupos",
    subtitle: "Aventuras compartidas, recuerdos multiplicados",
    description: "Viajar en grupo es una experiencia única. Ya sea con amigos, compañeros de trabajo o asociaciones, organizamos viajes donde la diversión está garantizada y cada detalle está coordinado.",
    image: "https://images.unsplash.com/photo-1527525443983-6e60c75fff46?auto=format&fit=crop&q=80&w=2000",
    hasGroupForm: true
  },
  cruceros: {
    title: "Cruceros",
    subtitle: "Navega hacia nuevas aventuras",
    description: "Descubre el mundo desde el mar. Los cruceros ofrecen la combinación perfecta de comodidad, entretenimiento y descubrimiento. Múltiples destinos, un solo viaje inolvidable.",
    image: "https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&q=80&w=2000"
  },
  bienestar: {
    title: "Bienestar y Relax",
    subtitle: "Desconecta y encuentra tu equilibrio",
    description: "Spas de lujo, retiros wellness, yoga, meditación y tratamientos holísticos. Destinos diseñados para tu desconexión total: Bali, Maldivas, Tailandia, Suiza... Cuida de ti mientras descubres lugares paradisíacos.",
    image: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&q=80&w=2000"
  },
  arte: {
    title: "Arte e Historia",
    subtitle: "Descubre el patrimonio cultural del mundo",
    description: "Recorre museos, galerías, sitios arqueológicos y ciudades históricas. Roma, Atenas, París, Egipto... Vive el arte y la historia con guías expertos que darán vida a cada rincón cultural.",
    image: "https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&q=80&w=2000"
  },
  lujo: {
    title: "Lujo y Compras",
    subtitle: "Experiencias premium y shopping exclusivo",
    description: "Milán, París, Nueva York, Dubai... Combina compras en las mejores boutiques con experiencias de lujo: hoteles 5 estrellas, restaurantes Michelin, acceso VIP y personal shopper. El lujo a tu medida.",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=2000"
  },
  deportivo: {
    title: "Deportivo y Cultural",
    subtitle: "Combina deporte y cultura",
    description: "Maratones, ciclismo, golf, tenis... Participa en eventos deportivos mientras descubres la cultura local. Incluyetraining camps, eventos internacionales y experiencias que unen tu pasión por el deporte con el descubrimiento cultural.",
    image: "https://images.unsplash.com/photo-1517649763962-0c623066013b?auto=format&fit=crop&q=80&w=2000"
  },
  enologico: {
    title: "Enológicos y Gastronómicos",
    subtitle: "Rutas del vino y experiencias gourmet",
    description: "Burdeos, Toscana, Rioja, Napa Valley... Descubre bodegas exclusivas, catas con sumilleres expertos, cenas maridaje y experiencias gastronómicas únicas. Para verdaderos amantes del buen vino y la alta cocina.",
    image: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&q=80&w=2000"
  },
  naturaleza: {
    title: "Naturaleza y Aventura",
    subtitle: "Safari, trekking y experiencias outdoor",
    description: "Safari en África, trekking en Patagonia, Islandia, Nueva Zelanda, Costa Rica... Vive la naturaleza en estado puro con actividades de aventura: senderismo, kayak, escalada, avistamiento de fauna. Para espíritus aventureros.",
    image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&q=80&w=2000"
  },
  medida: {
    title: "Viajes a Medida",
    subtitle: "Tu viaje, tus reglas",
    description: "¿Tienes un sueño viajero? Nosotros lo hacemos realidad. Diseñamos cada detalle según tus preferencias: destino, duración, actividades, alojamiento... Todo personalizado para ti.",
    image: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&q=80&w=2000"
  }
};

export default function TripCategory() {
  const { category } = useParams();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    contact_preference: '',
    num_travelers: 2,
    group_name: '',
    budget: '',
    special_requests: ''
  });

  const info = CATEGORY_INFO[category] || {
    title: "Viajes",
    subtitle: "Descubre nuestras ofertas",
    description: "",
    image: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&q=80&w=2000"
  };

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await axios.get(`${API}/trips?category=${category}`);
        setTrips(res.data);
      } catch (err) {
        console.error('Error fetching trips:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, [category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        contact_preference: formData.contact_preference,
        subject: `Consulta ${info.title}`,
        message: `
          Categoría: ${info.title}
          Viajeros: ${formData.num_travelers}
          ${formData.group_name ? `Agrupación: ${formData.group_name}` : ''}
          ${formData.budget ? `Presupuesto: ${formData.budget}` : ''}
          Comentarios: ${formData.special_requests}
        `
      });
      toast.success('¡Solicitud enviada! Te contactaremos pronto.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        contact_preference: '',
        num_travelers: 2,
        group_name: '',
        budget: '',
        special_requests: ''
      });
    } catch (err) {
      toast.error('Error al enviar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={info.image}
          alt={info.title}
          className="w-full h-full object-cover"
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="container-custom">
            <h1 
              className="text-4xl lg:text-6xl font-serif text-white mb-4"
              data-testid="category-title"
            >
              {info.title}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl">
              {info.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Description */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">
              {info.description}
            </p>
          </div>
        </div>
      </section>

      {/* Trips Grid - oculto para categoría medida */}
      {category !== 'medida' && (
        <section className="section bg-slate-50">
          <div className="container-custom">
            <h2 className="section-title mb-12">Ofertas disponibles</h2>

            {loading ? (
              <div className="grid-trips">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="trip-card skeleton h-[400px]" />
                ))}
              </div>
            ) : trips.length > 0 ? (
              <div className="grid-trips">
                {trips.map((trip, index) => (
                  <Link
                    key={trip.id}
                    to={`/viaje/${trip.id}`}
                    className="trip-card group hover-lift"
                    data-testid={`category-trip-${index}`}
                  >
                    <div className="trip-card-image-wrapper">
                      {trip.discount && (
                        <div className="absolute top-4 left-4 z-30 bg-accent text-white px-4 py-1.5 rounded-full font-bold text-sm shadow-xl flex items-center gap-2 animate-pulse border-2 border-white/20">
                          <Tag className="w-4 h-4" />
                          {trip.discount}
                        </div>
                      )}
                      <div 
                        className="absolute inset-0 bg-cover bg-center scale-110 blur-xl opacity-30"
                        style={{ backgroundImage: `url(${trip.image_url})` }}
                      />
                      <img
                        src={trip.image_url}
                        alt={trip.title}
                        className="relative z-10 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="trip-card-overlay z-20" />
                    </div>
                    <div className="trip-card-content">
                      <span className="flex items-center gap-1 text-secondary text-xs uppercase tracking-widest font-bold mb-1">
                        {trip.destination}
                      </span>
                      <h3 className="text-xl font-serif font-bold mt-1 mb-4 line-clamp-2 min-h-[3.5rem]">
                        {trip.title}
                      </h3>
                      <div className="mt-auto flex items-center justify-between border-t border-white/10 pt-4">
                        <span className="flex items-center gap-1 text-white/70 text-sm">
                          <Clock className="w-4 h-4 text-accent" />
                          {trip.duration}
                        </span>
                        <div className="text-right">
                          <span className="block text-xs text-white/60 uppercase tracking-tighter">Desde</span>
                          <span className="text-accent font-bold text-xl">
                            {trip.price.toLocaleString('es-ES')}€
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-6">
                  No hay ofertas disponibles en esta categoría actualmente.
                </p>
                <p className="text-primary font-medium">
                  ¡Pero podemos crear tu viaje a medida! Rellena el formulario de contacto.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Contact Form */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">¿Te ayudamos?</h2>
              <p className="section-subtitle mx-auto">
                Cuéntanos qué tienes en mente y diseñaremos tu viaje perfecto
              </p>
            </div>

            <form 
              onSubmit={handleSubmit} 
              className="bg-slate-50 p-8 rounded-2xl"
              data-testid="category-contact-form"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="form-label">Nombre completo</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Tu nombre"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="tu@email.com"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <Input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+34 612 345 678"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Preferencia de contacto *</label>
                  <Select
                    required
                    value={formData.contact_preference}
                    onValueChange={(value) => setFormData({ ...formData, contact_preference: value })}
                  >
                    <SelectTrigger className="form-input">
                      <SelectValue placeholder="Selecciona una opción" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="llamada">Llamada</SelectItem>
                      <SelectItem value="videollamada">Videollamada (WhatsApp)</SelectItem>
                      <SelectItem value="presencial">Reunión presencial (por ahora solo en Comunidad de Madrid)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Número de viajeros
                  </label>
                  <Input
                    type="number"
                    min="1"
                    required
                    value={formData.num_travelers}
                    onChange={(e) => setFormData({ ...formData, num_travelers: parseInt(e.target.value) })}
                    className="form-input"
                  />
                </div>

                {info.hasGroupForm && (
                  <>
                    <div className="form-group">
                      <label className="form-label flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Nombre del grupo/asociación
                      </label>
                      <Input
                        value={formData.group_name}
                        onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                        placeholder="Ej: Club deportivo, empresa..."
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label flex items-center gap-2">
                        <Wallet className="w-4 h-4" />
                        Presupuesto máximo por persona
                      </label>
                      <Input
                        value={formData.budget}
                        onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                        placeholder="Ej: 500€, 1000€..."
                        className="form-input"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="form-group mt-6">
                <label className="form-label">Cuéntanos más sobre tu viaje ideal</label>
                <Textarea
                  value={formData.special_requests}
                  onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                  placeholder="Fechas aproximadas, destinos que te interesan, actividades..."
                  className="form-textarea"
                  rows={4}
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full btn-accent py-6 mt-6"
              >
                {submitting ? 'Enviando...' : (
                  <>
                    Solicitar Información
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <div className="h-32 bg-slate-50" />
    </Layout>
  );
}
